import React, { useState, useRef, useEffect, Fragment, useMemo } from "react";
import { ExpandMore, GetApp, ChevronRight, UnfoldMore } from "@material-ui/icons";
import _ from "lodash";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getExpandedRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import MixpanelService from "../utils/MixpanelService";

export default function ClaimsTable(props) {
  const tableContainerRef = useRef();

  const [sorting, setSorting] = useState([]);

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("name", {
      header: "Report",
      enableSorting: false,
      cell: ({ row, getValue }) => {
        return (
          <div className="table__text__stack">
            <div className="t--md t--500">{getValue()}</div>
            <div className="t--sm t--subtitle">{row.original.subtitle}</div>
          </div>
        );
      },
    }),
    columnHelper.accessor("link", {
      header: "Action",
      enableSorting: false,
      cell: ({ row, renderValue }) => {
        const mixPanelTrackerId = row.original.mixPanelTrackerId;

        return (
          <div className="t--sm t--500">
            <a href={renderValue()} onClick={() => MixpanelService.track(mixPanelTrackerId)}>
              <div className="btn btn--light">View Report</div>
            </a>
          </div>
        );
      },
    }),
  ];

  const reports = [
    {
      name: "Pharmacy Claims Details",
      link: "reports/pharmacy_claim_details",
      subtitle: "View submitted pharmacy claims",
      mixPanelTrackerId: "esp_pharmacy_claims_view_report_clicked",
    },
    {
      name: "Pharmacy Encounter Claims Details",
      link: "reports/pharmacy_encounter_claim_details",
      subtitle: "View submitted pharmacy encounter claims",
      mixPanelTrackerId: "esp_encounter_claims_view_report_clicked",
    },
    {
      name: "Medical Claims Details",
      link: "reports/medical_claim_details",
      subtitle: "View submitted medical claims",
      mixPanelTrackerId: "esp_medical_claims_view_report_clicked",
    },
    {
      name: "Medical Encounter Claims Details",
      link: "reports/medical_encounter_claim_details",
      subtitle: "View submitted medical encounter claims",
      mixPanelTrackerId: "esp_medical_encounter_claims_view_report_clicked",
    },
    {
      name: "Single Contract Pharmacy Designations",
      link: "reports/designations",
      subtitle: "View designations for all manufacturers",
      mixPanelTrackerId: "esp_designations_view_report_clicked",
    },
    {
      name: "At Risk and Suspended Pharmacies",
      link: "reports/at_risk",
      subtitle: "View pharmacies identified as having incomplete 340B claims submissions",
      mixPanelTrackerId: "esp_at_risk_cps_view_report_clicked",
    },
  ];

  const table = useReactTable({
    data: reports,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 50,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const renderVirtualRows = () =>
    virtualRows.map((virtualRow) => {
      const row = rows[virtualRow.index];
      return (
        <tr
          key={row.id}
          className="sm"
          ref={rowVirtualizer.measureElement}
          data-index={virtualRow.index}
        >
          {row.getVisibleCells().map((cell) => (
            <td key={cell.id} className="t--sm">
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
        </tr>
      );
    });

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  return (
    <>
      <div ref={tableContainerRef} className="virtual__table__reports__container">
        <table className="primary">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="sm">
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <span
                        style={{ fontSize: 12, display: "inline-flex", verticalAlign: "top" }}
                        onClick={() => header.column.toggleSorting()}
                      >
                        <UnfoldMore fontSize="inherit" color="inherit" />
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr>
                <td colSpan={columns.length} style={{ height: `${paddingTop}px` }} />
              </tr>
            )}

            {renderVirtualRows()}
            {paddingBottom > 0 && (
              <tr>
                <td colSpan={columns.length} style={{ height: `${paddingBottom}px` }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
